import type { ReactNode } from "react"
import _t from "@core/i18n"
import type { Formily as FormilyType } from "@gatsby-plugin-generic-page/fragments/formily"
import useCurrentPlanSettings from "@gatsby-plugin-marketplace/hooks/useCurrentPlanSettings"
import { chevronRight } from "@ionos-web-design-system/icon/system"
import type { CardTariffProps } from "@ionos-web-design-system/react"
import { Button, Link, Text } from "@ionos-web-design-system/react"
import get from "lodash/get"
import DynamicUdsIcon from "~/components/DynamicUdsIcon"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import isEmpty from "~/lib/isEmpty"
import { getPlansUsageOverLimit } from "~/store/basket/selectors"
import { useAppDispatch, useAppSelector } from "~/store/hooks"
import type { AppState } from "~/store/reducer"
import type { ParametersUDSTableProps } from ".."
import PackageSelect from "../components/PackageSelect"
import getProductPriceData from "../helpers/getProductPriceData"
import getTariffCardButtonProps from "../helpers/getTariffCardButtonProps"
import type { PackageDropdown } from "../types"
import useProductDetailPageLinks from "./useProductDetailPageLinks"

type UseHeaderTariffCardsProps = Omit<ParametersUDSTableProps, "packages"> & {
  /** Package dropdown state for each product column (one entry per product). */
  dropdowns: PackageDropdown[]
  /** Callback invoked when the user clicks a button that opens a Formily form modal. */
  onFormilyFormClick: (form: FormilyType) => void
}

type HeaderTariffCardHookResult = {
  tariffCardsColumns: CardTariffProps[]
}

/**
 * Builds the `CardTariffProps` array consumed by the comparison table header columns.
 *
 * For every product in `products` it:
 * - resolves the price data for the currently selected period and price type,
 * - determines the CTA button label + handler via `getTariffCardButtonProps`,
 * - picks the correct footer element: package selector, "currently viewing" label,
 *   or a link to the product detail page,
 * - marks the recommended product with a badge.
 *
 * Layout rule for `CardTariffProps` slots:
 * - When a CTA button exists → `button` = CTA, `children` = link/footer element.
 * - When no CTA button → `button` = link/footer element, `children` = undefined.
 *
 * @param products - List of comparable products to render as columns.
 * @param dropdowns - Package dropdown state for each product column (one entry per product).
 * @param saleConfiguration - Active sale configuration used to determine the currently viewed product.
 * @param selectedPeriod - Billing period currently selected by the user (e.g. monthly/yearly).
 * @param customLinkText - Optional override for the product detail page link label.
 * @param onFormilyFormClick - Callback invoked when the user clicks a button that opens a Formily form modal.
 * @param showCurrentViewing - Whether to display the "currently viewing" badge on the active product column.
 * @param recommendedComparableProductId - ID of the product that should be highlighted with a recommendation badge.
 *
 * @returns An object with `tariffCardsColumns` — one entry per product, in the same order.
 */
export default function useHeaderTariffCards({
  products,
  dropdowns,
  saleConfiguration,
  selectedPeriod,
  customLinkText,
  onFormilyFormClick,
  showCurrentViewing,
  recommendedComparableProductId,
}: UseHeaderTariffCardsProps): HeaderTariffCardHookResult {
  const currentPlanSettings = useCurrentPlanSettings(saleConfiguration)
  const { currentPriceType } = usePriceTypeContext()

  const dispatch = useAppDispatch()

  const plansOverLimit = useAppSelector(getPlansUsageOverLimit)
  const marketplaceConfiguration = useAppSelector(
    (state: AppState) => state.marketplace.configuration
  )

  // Alias of the CloudBlue plan the user is currently subscribed to (if any).
  const currentPlanAlias = get(
    currentPlanSettings,
    "cloudBluePlan[0].flatData.alias"
  ) as string | undefined

  const productPageLinks = useProductDetailPageLinks(products ?? [])

  if (isEmpty(products)) {
    return { tariffCardsColumns: [] }
  }

  const tariffCardsColumns: CardTariffProps[] = products.map(
    (product, index) => {
      const columnDropdown = dropdowns[index]

      const itemSaleConfigId = product?.flatData.saleConfigurations?.[0]?.id
      const itemDefaultPlanAlias = get(
        product,
        "flatData.saleConfigurations[0].flatData.defaultCloudBluePlan[0].flatData.alias"
      ) as string | undefined

      // True when this product is the one currently shown on the detail page.
      const isSaleConfigActive =
        !!saleConfiguration?.id && saleConfiguration.id === itemSaleConfigId

      // True when the user's active plan matches this product's default plan.
      const isDefaultPlanActive =
        !!itemSaleConfigId && itemDefaultPlanAlias === currentPlanAlias

      // "Currently viewing" is only meaningful in a multi-product comparison (single-product
      // tables always show the one product — highlighting it would be redundant).
      const isCurrentViewing =
        products.length > 1 ? isSaleConfigActive || isDefaultPlanActive : false

      // Marks the recommended/highlighted column with a badge.
      const isActive = !isEmpty(recommendedComparableProductId)
        ? product?.id === recommendedComparableProductId
        : false

      const buttonData = getTariffCardButtonProps(
        product,
        saleConfiguration?.id,
        selectedPeriod,
        dispatch,
        plansOverLimit,
        currentPriceType,
        marketplaceConfiguration,
        onFormilyFormClick
      )

      // Card link element priority:
      // 1. PackageSelect dropdown (when the column has selectable packages).
      // 2. "Currently viewing" label (when the user is already on this plan).
      // 3. Link to the product detail page (fallback).
      let cardLinkElement: ReactNode = null
      const pageLinkProps = productPageLinks[index] ?? null

      if (columnDropdown) {
        cardLinkElement = (
          <PackageSelect
            items={columnDropdown.items}
            label={columnDropdown.label}
          />
        )
      } else if (showCurrentViewing && isCurrentViewing) {
        cardLinkElement = (
          <Text
            className="flex justify-center items-center"
            variant="bodySm"
            weight="black"
            color="promotion"
          >
            <DynamicUdsIcon iconName="visibility" className="mr-1 shrink-0" />

            <span>{_t("comparisonTable.currentlyViewing")}</span>
          </Text>
        )
      } else if (pageLinkProps) {
        cardLinkElement = (
          <Text variant="bodySm" className="text-center">
            <Link
              href={pageLinkProps.href}
              onClick={pageLinkProps.onClick}
              concept="mono"
              size="small"
              icon={chevronRight}
              position="suffix"
              className="text-base font-normal"
            >
              {customLinkText ?? _t("comparisonTable.linkDesc")}
            </Link>
          </Text>
        )
      }

      const priceData =
        getProductPriceData(
          product,
          currentPriceType,
          currentPlanSettings,
          saleConfiguration,
          selectedPeriod
        ) ?? undefined

      // When a CTA button exists it occupies the primary `button` slot and the link
      // moves to `children` (rendered below the button). Without a CTA the link
      // takes the primary `button` slot directly.
      //
      // Non-recommended plans use "monochrome"; recommended (isActive) use "callout" — matches Tariff Cards.
      return {
        title: product.flatData.name,
        button: !isEmpty(buttonData) ? (
          <Button
            concept={isActive ? "callout" : "monochrome"}
            size="small"
            className="w-full"
            onClick={buttonData.onClick}
          >
            {buttonData.text}
          </Button>
        ) : (
          cardLinkElement
        ),
        children: !isEmpty(buttonData) ? cardLinkElement : undefined,
        badgeText: isActive ? _t("comparisonTable.badge") : undefined,
        priceData: !isEmpty(priceData)
          ? { ...priceData, reservePrelineSpace: true }
          : undefined,
      }
    }
  )

  return {
    tariffCardsColumns,
  }
}
